import { appendMetaAccountToUrl, readStoredMetaAdAccount } from "./metaAdAccounts";

export const DASHBOARD_BASE = "/api/zocometa/dashboard";

export const defaultFilters = () => {
    const now = new Date();
    const to = now.toISOString().slice(0, 10);
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - 29);
    const from = fromDate.toISOString().slice(0, 10);
    return {
        fechaDesde: from,
        fechaHasta: to,
        campania: "",
        estado: "",
        buscar: "",
    };
};

export const buildDashboardUrl = (path, token, filters = {}) => {
    const params = new URLSearchParams();
    params.set("token", token);

    if (filters.fechaDesde) params.set("fechaDesde", filters.fechaDesde);
    if (filters.fechaHasta) params.set("fechaHasta", filters.fechaHasta);
    if (filters.campania) params.set("campania", filters.campania);
    if (filters.estado) params.set("estado", filters.estado);
    if (filters.buscar) params.set("buscar", filters.buscar);
    if (filters.adAccountId) params.set("adAccountId", filters.adAccountId);
    if (filters.adAccountKey) params.set("adAccountKey", filters.adAccountKey);

    return appendMetaAccountToUrl(`${DASHBOARD_BASE}/${path}?${params.toString()}`, readStoredMetaAdAccount());
};

export const fetchDashboard = async (path, token, filters = {}) => {
    const response = await fetch(buildDashboardUrl(path, token, filters));
    if (response.status === 401 || response.status === 403) {
        const error = new Error("unauthorized");
        error.code = response.status;
        throw error;
    }

    if (!response.ok) {
        const text = await response.text();
        try {
            const parsed = JSON.parse(text);
            throw new Error(parsed?.error || text || `Error ${response.status}`);
        } catch {
            throw new Error(text || `Error ${response.status}`);
        }
    }

    return response.json();
};

export const fetchCampaignOptions = async (token, filters = {}) => {
    const payload = await fetchDashboard("campanaszt", token, {
        fechaDesde: filters.fechaDesde || "",
        fechaHasta: filters.fechaHasta || "",
        campania: "",
        estado: "",
        buscar: "",
    });

    return Array.isArray(payload?.campaignOptions)
        ? payload.campaignOptions
            .map((item) => ({
                id: item?.id || item?.value || "",
                nombre: item?.nombre || item?.name || item?.label || item?.id || item?.value || "",
                estado: item?.estado || item?.status || "",
                label: item?.label || "",
            }))
            .filter((item) => item.id && item.nombre)
        : [];
};

export const postZocoMetaIaAnalisis = async (token, filters = {}) => {
    const selectedAccount = readStoredMetaAdAccount();
    const response = await fetch(appendMetaAccountToUrl("/api/zocometa/ai/analisis", selectedAccount), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            token,
            fechaDesde: filters.fechaDesde || null,
            fechaHasta: filters.fechaHasta || null,
            campania: filters.campania || null,
            estado: filters.estado || null,
            buscar: filters.buscar || null,
            adAccountId: selectedAccount?.adAccountId || null,
            adAccountKey: selectedAccount?.key || null,
        }),
    });

    if (response.status === 401 || response.status === 403) {
        const error = new Error("unauthorized");
        error.code = response.status;
        throw error;
    }

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Error ${response.status}`);
    }

    return response.json();
};

const readResponseOrThrow = async (response) => {
    if (response.status === 401 || response.status === 403) {
        const error = new Error("unauthorized");
        error.code = response.status;
        throw error;
    }
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Error ${response.status}`);
    }
    return response.json();
};

export const fetchZocoMetaAiKnowledge = async (token, incluirInactivos = false) => {
    const params = new URLSearchParams();
    params.set("token", token);
    if (incluirInactivos) params.set("incluirInactivos", "true");
    const response = await fetch(`/api/zocometa/ai/knowledge?${params.toString()}`);
    return readResponseOrThrow(response);
};

export const postZocoMetaAiKnowledge = async (payload) => {
    const response = await fetch("/api/zocometa/ai/knowledge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return readResponseOrThrow(response);
};

export const putZocoMetaAiKnowledge = async (id, payload) => {
    const response = await fetch(`/api/zocometa/ai/knowledge/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return readResponseOrThrow(response);
};

export const deleteZocoMetaAiKnowledge = async (token, id) => {
    const params = new URLSearchParams();
    params.set("token", token);
    const response = await fetch(`/api/zocometa/ai/knowledge/${id}?${params.toString()}`, {
        method: "DELETE",
    });
    return readResponseOrThrow(response);
};

export const fetchZocoMetaAiHistorial = async (token, take = 50) => {
    const params = new URLSearchParams();
    params.set("token", token);
    params.set("take", String(take));
    const response = await fetch(`/api/zocometa/ai/historial?${params.toString()}`);
    return readResponseOrThrow(response);
};

export const fetchZocoMetaAiFeedback = async (token, analisisId = null) => {
    const params = new URLSearchParams();
    params.set("token", token);
    if (analisisId) params.set("analisisId", String(analisisId));
    const response = await fetch(`/api/zocometa/ai/feedback?${params.toString()}`);
    return readResponseOrThrow(response);
};

export const postZocoMetaAiFeedback = async (payload) => {
    const response = await fetch("/api/zocometa/ai/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return readResponseOrThrow(response);
};

export const fetchZocoMetaAiDebugContext = async (token, filters = {}) => {
    const params = new URLSearchParams();
    params.set("token", token);
    if (filters.fechaDesde) params.set("fechaDesde", filters.fechaDesde);
    if (filters.fechaHasta) params.set("fechaHasta", filters.fechaHasta);
    if (filters.campania) params.set("campaignId", filters.campania);
    if (filters.estado) params.set("estado", filters.estado);
    if (filters.buscar) params.set("buscar", filters.buscar);
    const selectedAccount = readStoredMetaAdAccount();
    const response = await fetch(appendMetaAccountToUrl(`/api/zocometa/ai/debug-context?${params.toString()}`, selectedAccount));
    return readResponseOrThrow(response);
};

export const fetchZocoMetaAiHistorialReporte = async (token, id) => {
    const params = new URLSearchParams();
    params.set("token", token);
    const response = await fetch(`/api/zocometa/ai/historial/${id}/reporte?${params.toString()}`);
    const payload = await readResponseOrThrow(response);
    return payload && typeof payload === "object"
        ? { ...payload, reporte: payload }
        : payload;
};
